/**
 * The Tiptap editor (`061-editor.md`).
 *
 * **Tiptap owns the document while the editor is mounted.** The ProseMirror `EditorState`
 * is the single source of truth for the text on screen; it is never mirrored into the
 * Query cache. Putting it there would re-render a React tree on every character and give
 * us two representations of one document to keep in sync — the exact bug `031` refuses to
 * have on the server, reintroduced on the client (`111` §5).
 *
 * The paste pipeline (M3) hangs off exactly three lines here — `transformPastedHTML` in
 * `editorProps`, `editorExtensions(ctx)` for the `FileHandler`, and `onSettled` wiring the
 * upload manager back to the save. Everything else about it lives in `paste/` and
 * `uploads.ts`, as pure functions that can be tested without a browser. That is deliberate:
 * `061` §4 calls the sanitizer "a pure, unit-tested function… not a pile of `if`s inside an
 * editor callback".
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  EditorContent,
  useEditor,
  useEditorState,
  type Editor as TiptapEditor,
} from '@tiptap/react';
// v3 moved the menus to their own entry point. Importing `BubbleMenu` from the package
// root type-checks against nothing and fails at runtime.
import { BubbleMenu } from '@tiptap/react/menus';
import {
  Baseline,
  Bold,
  Code,
  Heading1,
  Heading2,
  Heading3,
  Highlighter,
  Italic,
  List,
  ListChecks,
  ListOrdered,
  Quote,
  Strikethrough,
  Underline,
} from 'lucide-react';
import { emptyDoc, type PageDto, type ProseMirrorDoc } from '@/api/endpoints/pages';
import { cn } from '@/lib/utils';
import { deepEqual } from '@/lib/content';
import { useHotkeys, type Hotkey } from '@/hooks/useHotkeys';
import { useTranslation } from '@/lib/i18n';
import { toast } from '@/components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { ColorMenu } from '@/features/editor/ColorMenu';
import { editorExtensions } from '@/features/editor/extensions';
import { buildGatheredContent } from '@/features/editor/gatherChildren';
import type { PaletteColor } from '@/features/editor/marks';
import {
  inspectPastedHTML,
  transformPastedHTML,
} from '@/features/editor/paste/transformPastedHTML';
import { registerUploadTarget } from '@/features/editor/uploadBridge';
import { SearchPanel } from '@/features/editor/SearchPanel';
import { WikiLinkProvider, type WikiLinkActions } from '@/features/editor/wikiLinkContext';
import { restorePendingUploads, type UploadContext } from '@/features/editor/uploads';
import { Toolbar } from '@/features/editor/Toolbar';
import { useAutosave, useFlushOnBlur, type Autosave } from '@/features/editor/useAutosave';
import type { SaveStateKind } from '@/features/editor/SaveState';

export interface EditorProps {
  pageId: string;
  title: string;
  /** The initial document. Tiptap owns it from here; this is not a controlled prop. */
  content: ProseMirrorDoc;
  /** The `updatedAt` this document was based on — the save's precondition. */
  baseUpdatedAt: string;
  /** False while a conflict banner is open, or when the page is archived. */
  editable: boolean;
  save: SaveStateKind;
  /**
   * Put the caret in the body on mount. The `Mod+N` → name → `Enter` hand-off (`060` §6):
   * "Enter commits the title and OPENS THE PAGE, placing the caret in the editor body.
   * Typing continues without a second thought."
   */
  autoFocus: boolean;
  onOpenConflict: () => void;
  /** Fired once, right after mount, when a draft was restored and must be flushed (§7.1). */
  onReady?: (flush: () => void) => void;
  /**
   * Rendered inside the editor's SCROLL CONTAINER, below the document — the backlinks panel
   * (`070` §7.6). It belongs in the document flow, not in a right-hand drawer, "because it
   * is part of the page's meaning". A prop rather than a hard-coded import, because the
   * editor may not import from `features/notebook` (`111` §2).
   */
  footer?: React.ReactNode;
  /**
   * Clicking a BROKEN wiki link creates that page (`070` §7.2). Where it goes is a notebook
   * decision, not an editor one — see `wikiLinkContext.ts`.
   */
  onCreateLinkedPage?: (label: string) => void;
  /**
   * Resolve and fetch this page's DIRECT children, in tree order (`061` §2, "Gather
   * sub-pages"). Reading the tree and fetching bodies is a notebook concern — the editor may
   * not import from `features/notebook` (`111` §2) — so the host supplies it. When absent the
   * toolbar button is hidden. The editor owns the rest: building the combined document,
   * replacing its own content, and persisting through autosave.
   */
  onGatherChildren?: () => Promise<PageDto[]>;
}

function BubbleItem({
  label,
  active,
  onClick,
  children,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      onMouseDown={(event) => {
        event.preventDefault();
      }}
      onClick={onClick}
      className={cn(
        'flex size-6 items-center justify-center rounded-sm text-[var(--muted-foreground)]',
        'hover:bg-[var(--accent)] hover:text-[var(--foreground)]',
        active && 'bg-[var(--accent)] text-[var(--foreground)]',
        '[&_svg]:size-3.5',
      )}
    >
      {children}
    </button>
  );
}

function Bubble({ editor }: { editor: TiptapEditor }) {
  const { t } = useTranslation();
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive('bold'),
      italic: e.isActive('italic'),
      underline: e.isActive('underline'),
      strike: e.isActive('strike'),
      code: e.isActive('code'),
      highlightColor: (e.getAttributes('highlight')['color'] ?? null) as PaletteColor | null,
      textColorValue: (e.getAttributes('textColor')['color'] ?? null) as PaletteColor | null,
      h1: e.isActive('heading', { level: 1 }),
      h2: e.isActive('heading', { level: 2 }),
      h3: e.isActive('heading', { level: 3 }),
      quote: e.isActive('blockquote'),
      bullet: e.isActive('bulletList'),
      ordered: e.isActive('orderedList'),
      task: e.isActive('taskList'),
    }),
  });

  const chain = () => editor.chain().focus();

  return (
    <BubbleMenu
      editor={editor}
      // Marks are meaningless inside a code block, so the menu does not appear there.
      shouldShow={({ editor: e, from, to }: { editor: TiptapEditor; from: number; to: number }) =>
        from !== to && !e.isActive('codeBlock')
      }
      className="flex items-center gap-0.5 rounded-lg border border-[var(--border)] bg-[var(--popover)] p-1 shadow-md"
    >
      <BubbleItem
        label={t('editor.toolbar.bold')}
        active={state.bold}
        onClick={() => chain().toggleBold().run()}
      >
        <Bold />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.italic')}
        active={state.italic}
        onClick={() => chain().toggleItalic().run()}
      >
        <Italic />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.underline')}
        active={state.underline}
        onClick={() => chain().toggleUnderline().run()}
      >
        <Underline />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.strikethrough')}
        active={state.strike}
        onClick={() => chain().toggleStrike().run()}
      >
        <Strikethrough />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.inlineCode')}
        active={state.code}
        onClick={() => chain().toggleCode().run()}
      >
        <Code />
      </BubbleItem>

      <span aria-hidden className="mx-1 h-4 w-px bg-[var(--border)]" />

      <ColorMenu
        variant="highlight"
        label={t('editor.color.highlight')}
        icon={<Highlighter />}
        active={state.highlightColor}
        onSelect={(color) => {
          if (color === null) chain().unsetHighlight().run();
          else chain().setHighlight({ color }).run();
        }}
      />
      <ColorMenu
        variant="text"
        label={t('editor.color.textColor')}
        icon={<Baseline />}
        active={state.textColorValue}
        onSelect={(color) => {
          if (color === null) chain().unsetTextColor().run();
          else chain().setTextColor({ color }).run();
        }}
      />

      <span aria-hidden className="mx-1 h-4 w-px bg-[var(--border)]" />

      <BubbleItem
        label={t('editor.toolbar.heading1')}
        active={state.h1}
        onClick={() => chain().toggleHeading({ level: 1 }).run()}
      >
        <Heading1 />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.heading2')}
        active={state.h2}
        onClick={() => chain().toggleHeading({ level: 2 }).run()}
      >
        <Heading2 />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.heading3')}
        active={state.h3}
        onClick={() => chain().toggleHeading({ level: 3 }).run()}
      >
        <Heading3 />
      </BubbleItem>

      <span aria-hidden className="mx-1 h-4 w-px bg-[var(--border)]" />

      <BubbleItem
        label={t('editor.toolbar.quote')}
        active={state.quote}
        onClick={() => chain().toggleBlockquote().run()}
      >
        <Quote />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.bulletList')}
        active={state.bullet}
        onClick={() => chain().toggleBulletList().run()}
      >
        <List />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.numberedList')}
        active={state.ordered}
        onClick={() => chain().toggleOrderedList().run()}
      >
        <ListOrdered />
      </BubbleItem>
      <BubbleItem
        label={t('editor.toolbar.taskList')}
        active={state.task}
        onClick={() => chain().toggleTaskList().run()}
      >
        <ListChecks />
      </BubbleItem>
    </BubbleMenu>
  );
}

/**
 * The current selection as a seed for the find field — the browser/VS-Code habit of pre-filling
 * find with the selected word. Only a short, single-line selection; a paragraph-long selection is
 * not a search term.
 */
function selectionSeed(editor: TiptapEditor): string {
  const { from, to, empty } = editor.state.selection;
  if (empty) return '';
  const text = editor.state.doc.textBetween(from, to, ' ');
  return text.length <= 100 && !text.includes('\n') ? text : '';
}

export function Editor({
  pageId,
  title,
  content,
  baseUpdatedAt,
  editable,
  save,
  autoFocus,
  onOpenConflict,
  onReady,
  footer,
  onCreateLinkedPage,
  onGatherChildren,
}: EditorProps) {
  const { t, plural } = useTranslation();
  const autosave = useAutosave({
    pageId,
    title,
    baseUpdatedAt,
    frozen: !editable,
    initialContent: content,
  });

  // Refs, because the upload context must be built BEFORE the editor exists (it is baked
  // into the extension list) and yet has to reach both of them once they do.
  const editorRef = useRef<TiptapEditor | null>(null);
  const autosaveRef = useRef<Autosave>(autosave);
  autosaveRef.current = autosave;

  const ctx = useMemo<UploadContext>(
    () => ({
      pageId,
      onSettled: () => {
        // `062` §3 step 7: the LAST upload has resolved, so the document no longer contains
        // a `blob:` src and the save that was suppressed while it was in flight may finally
        // run. Nothing else would ever re-arm it.
        const e = editorRef.current;
        if (e === null) return;
        autosaveRef.current.saveNow(e.getJSON() as ProseMirrorDoc);
      },
    }),
    [pageId],
  );

  /** The paste toast (`061` §4.4, §4.7). What was dropped, and what to do instead. */
  const reportPaste = useCallback(
    (html: string): void => {
      const report = inspectPastedHTML(html);

      if (report.droppedLocalImages > 0) {
        toast(plural('editor.paste.droppedLocalImages', report.droppedLocalImages), {
          description: t('editor.paste.droppedLocalImagesDescription'),
        });
      }

      if (report.remoteImages > 0) {
        toast(plural('editor.paste.remoteImages', report.remoteImages), {
          description: t('editor.paste.remoteImagesDescription'),
        });
      }
    },
    [plural, t],
  );

  const editor = useEditor({
    extensions: editorExtensions(ctx),
    content,
    editable,
    // Tiptap's own autofocus, rather than a `focus()` call from an effect. The effect
    // approach loses the race: the editor's view is still attaching, and React's StrictMode
    // remount throws away the very instance that was focused. Letting Tiptap place the caret
    // as it builds the view is the one moment that is guaranteed to be after the view exists
    // and before anything can steal it.
    autofocus: autoFocus ? 'end' : false,
    injectCSS: false, // we style everything with Tailwind
    editorProps: {
      attributes: { class: 'prose-rpn focus:outline-none', spellcheck: 'true' },

      // THE SANITIZER (`061` §4). The only place we get to look at Word's output before
      // ProseMirror does, and therefore where all the ugliness is confined.
      transformPastedHTML,

      // Purely observational: it tells the user what the sanitizer had to throw away.
      // Returning `false` is not optional — `true` would cancel the paste itself.
      handlePaste: (_view, event) => {
        const html = event.clipboardData?.getData('text/html') ?? '';
        if (html !== '') reportPaste(html);
        return false;
      },
    },
    onUpdate: ({ editor: e }) => {
      autosave.onChange(e.getJSON() as ProseMirrorDoc);
    },
  });

  const wikiLinkActions = useMemo<WikiLinkActions>(
    () => ({
      createPage: (label: string) => {
        onCreateLinkedPage?.(label);
      },
    }),
    [onCreateLinkedPage],
  );

  /* ── Gather sub-pages (`061` §2) ──────────────────────────────────────────── */

  const [gathering, setGathering] = useState(false);
  // The fetched children waiting on the "replace this page?" confirmation. Null = no prompt.
  const [pendingGather, setPendingGather] = useState<PageDto[] | null>(null);

  const applyGather = useCallback(
    (children: PageDto[]) => {
      const e = editorRef.current;
      if (e === null) return;
      const nodes = buildGatheredContent(children, t('editor.gatherChildren.untitled'));
      // One transaction that replaces the whole body — so a single Ctrl+Z brings it back,
      // and the normal `onUpdate` autosave fires.
      e.chain().focus().insertContentAt({ from: 0, to: e.state.doc.content.size }, nodes).run();
      // Persist now rather than waiting on the 800ms debounce — the same door the upload
      // manager uses on `onSettled`, straight through the outbox.
      autosaveRef.current.saveNow(e.getJSON() as ProseMirrorDoc);
      toast.success(t('editor.gatherChildren.done', { count: children.length }));
    },
    [t],
  );

  const handleGather = useCallback(() => {
    if (onGatherChildren === undefined || gathering) return;
    void (async () => {
      setGathering(true);
      try {
        const children = await onGatherChildren();
        if (children.length === 0) {
          toast(t('editor.gatherChildren.noChildren'));
          return;
        }
        const e = editorRef.current;
        if (e === null) return;
        // An empty page just fills; a page that already has content asks first, because the
        // gather REPLACES the body.
        if (deepEqual(e.getJSON(), emptyDoc())) applyGather(children);
        else setPendingGather(children);
      } catch {
        toast.error(t('editor.gatherChildren.error'));
      } finally {
        setGathering(false);
      }
    })();
  }, [applyGather, gathering, onGatherChildren, t]);

  /* ── Find & replace (`110` §7) ────────────────────────────────────────────── */

  // `nonce` bumps on every open so the panel re-seeds and re-focuses even when already open.
  const [search, setSearch] = useState<{
    open: boolean;
    replace: boolean;
    nonce: number;
    seed: string;
  }>({ open: false, replace: false, nonce: 0, seed: '' });

  const openFind = useCallback(() => {
    const e = editorRef.current;
    setSearch((s) => ({
      open: true,
      replace: false,
      nonce: s.nonce + 1,
      seed: e === null ? '' : selectionSeed(e),
    }));
  }, []);

  const openReplace = useCallback(() => {
    const e = editorRef.current;
    setSearch((s) => ({
      open: true,
      replace: true,
      nonce: s.nonce + 1,
      seed: e === null ? '' : selectionSeed(e),
    }));
  }, []);

  const closeSearch = useCallback(() => {
    setSearch((s) => ({ ...s, open: false }));
  }, []);

  // `mod: true`, so both fire inside the editor and the dispatcher `preventDefault`s them —
  // reclaiming `Mod+F` from the browser's native find (a deliberate reversal of `110` §7, now
  // documented there). Registered here, so they exist only while a page is open.
  const searchHotkeys = useMemo<Hotkey[]>(
    () => [
      { key: 'f', mod: true, handler: openFind },
      { key: 'h', mod: true, shift: true, handler: openReplace },
    ],
    [openFind, openReplace],
  );
  useHotkeys(searchHotkeys);

  useFlushOnBlur(editor, autosave.flush);

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  useEffect(() => {
    if (editor === null) return;
    editorRef.current = editor;

    const unregister = registerUploadTarget(editor, ctx);

    // A pasted image whose upload never landed — the tab was killed, or the network was
    // gone. The blob is in IndexedDB; put it back on screen and try again. The user sees
    // their picture after a full browser restart even though the server never got it
    // (`062` §5).
    void restorePendingUploads(editor, ctx);

    return () => {
      unregister();
      editorRef.current = null;
    };
  }, [ctx, editor]);

  useEffect(() => {
    if (editor === null || onReady === undefined) return;
    onReady(autosave.flush);
    // Once per editor instance. `onReady` is the restored-draft flush (§7.1); firing it
    // twice would enqueue the same save twice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  if (editor === null) return null;

  return (
    // The wiki-link node views are portaled into THIS React tree (`ReactRenderer` →
    // `editor.contentComponent`), so the provider must sit above `EditorContent` — which is
    // exactly why it is here and not inside the extension list.
    <WikiLinkProvider value={wikiLinkActions}>
      <div className="relative flex min-h-0 flex-1 flex-col">
        <Toolbar
          editor={editor}
          save={save}
          uploads={ctx}
          onOpenConflict={onOpenConflict}
          onGatherChildren={onGatherChildren === undefined ? undefined : handleGather}
          gathering={gathering}
        />
        <Bubble editor={editor} />

        {search.open && (
          <SearchPanel
            editor={editor}
            editable={editable}
            nonce={search.nonce}
            seed={search.seed}
            replaceMode={search.replace}
            onClose={closeSearch}
            onReplacedAll={() => {
              autosaveRef.current.saveNow(editor.getJSON() as ProseMirrorDoc);
            }}
          />
        )}

        {/* One scroll container for the document AND the backlinks panel: the panel is part
            of the page, and a panel that does not scroll with the text it belongs to reads
            as chrome (`070` §7.6). */}
        <div className="min-h-0 flex-1 overflow-y-auto">
          <EditorContent
            editor={editor}
            data-testid="editor"
            // Full-width document (deliberate deviation from `110`'s readable-measure default):
            // the page fills the pane, with comfortable left/right padding instead of a centred
            // `max-w-[78ch]` column.
            className={cn('prose-editor w-full px-8 py-4', !editable && 'opacity-70')}
          />
          {footer}
        </div>
      </div>

      {/* Gather sub-pages replaces the whole body, so a page that already has content asks
          first. (An empty page skips this and fills straight away.) Undo still works, but the
          confirmation is the honest place to say what is about to happen. */}
      <AlertDialog
        open={pendingGather !== null}
        onOpenChange={(next) => {
          if (!next) setPendingGather(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('editor.gatherChildren.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingGather !== null &&
                t('editor.gatherChildren.confirmBody', { count: pendingGather.length })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                const children = pendingGather;
                setPendingGather(null);
                if (children !== null) applyGather(children);
              }}
            >
              {t('editor.gatherChildren.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </WikiLinkProvider>
  );
}
